import React, { useState, useEffect } from 'react';
import { 
  X, Save, Mail, Phone, MapPin, Calendar, Check, AlertCircle,
  User, Shield, FileText, Image, Fingerprint, Settings, UserCheck,
  Key, Clock, RefreshCw, CheckCircle, XCircle, ChevronDown, ChevronRight
} from 'lucide-react';

const UserEditModal = ({ user, isOpen, onClose, onSave, roles, tools }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    contactInfo: true,
    securityInfo: false,
    systemInfo: false
  });
  
  const [formData, setFormData] = useState({
    id : null,
    profile_picture: null,
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    bio: '',
    phone_number: '',
    date_of_birth: '',
    address: '',
    is_active: true,
    role: '',
    is_verified: false,
    roles: [], 
    tools: []
  });
  
  const [errors, setErrors] = useState({});
  
  // Initialize form with user data when modal opens
  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        id: user.id || null,
        profile_picture: user.profile_picture || null,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        username: user.username || '',
        bio: user.bio || '',
        phone_number: user.phone_number || '',
        date_of_birth: user.date_of_birth ? new Date(user.date_of_birth).toISOString().split('T')[0] : '',
        address: user.address || '',
        is_active: user.is_active !== undefined ? user.is_active : true,
        roles: user.roles || [], 
        tools: user.tools || [],
        is_verified: user.is_verified || false
      });
      setErrors({});
    }
  }, [user, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear validation error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  
  const validate = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    /*if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }*/
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone_number && !/^\+?[0-9]{10,15}$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Invalid phone number format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
  
    // Check if email was changed
    if (formData.email !== user.email) {
      const isConfirmed = window.confirm(
        `You are changing the email from ${user.email} to ${formData.email}. ` +
        `This will require the user to verify their new email address. Continue?`
      );
      if (!isConfirmed) return;
    }
  
    setIsSaving(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 800)); // Simulate API delay
      onSave(formData);
      setShowConfirmation(true);
      setTimeout(() => {
        setShowConfirmation(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error saving user:', error);
      setErrors({ submit: error.message || 'Failed to save user' });
    } finally {
      setIsSaving(false);
    }
  };
  
  // If modal is not open, don't render anything
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
      <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-screen overflow-hidden flex flex-col border border-gray-700">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center">
            <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-4 shadow-lg">
              <User size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Edit User Profile</h2>
              <p className="text-gray-400 text-sm">ID: {user?.id}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              user?.is_active 
                ? 'bg-green-900/50 text-green-300 border border-green-700'
                : 'bg-red-900/50 text-red-300 border border-red-700'
            }`}>
              {user?.is_active ? 'Active' : 'Inactive'}
            </div>
            <button 
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-gray-700/50 text-gray-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* Content Area with Tabs */}
        <div className="flex flex-1 overflow-hidden">
          {/* Tab Sidebar */}
          <div className="w-48 bg-gray-900 border-r border-gray-700 flex flex-col">
            <div 
              className={`px-4 py-3 flex items-center space-x-3 cursor-pointer hover:bg-gray-800 transition-colors ${activeTab === 'profile' ? 'bg-gray-800 border-l-2 border-blue-500' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <User size={18} className={activeTab === 'profile' ? 'text-blue-400' : 'text-gray-400'} />
              <span className={activeTab === 'profile' ? 'text-blue-400' : 'text-gray-400'}>Profile</span>
            </div>
            <div 
              className={`px-4 py-3 flex items-center space-x-3 cursor-pointer hover:bg-gray-800 transition-colors ${activeTab === 'security' ? 'bg-gray-800 border-l-2 border-blue-500' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              <Shield size={18} className={activeTab === 'security' ? 'text-blue-400' : 'text-gray-400'} />
              <span className={activeTab === 'security' ? 'text-blue-400' : 'text-gray-400'}>Security</span>
            </div>
            <div 
              className={`px-4 py-3 flex items-center space-x-3 cursor-pointer hover:bg-gray-800 transition-colors ${activeTab === 'preferences' ? 'bg-gray-800 border-l-2 border-blue-500' : ''}`}
              onClick={() => setActiveTab('preferences')}
            >
              <Settings size={18} className={activeTab === 'preferences' ? 'text-blue-400' : 'text-gray-400'} />
              <span className={activeTab === 'preferences' ? 'text-blue-400' : 'text-gray-400'}>Preferences</span>
            </div>
            <div 
              className={`px-4 py-3 flex items-center space-x-3 cursor-pointer hover:bg-gray-800 transition-colors ${activeTab === 'system' ? 'bg-gray-800 border-l-2 border-blue-500' : ''}`}
              onClick={() => setActiveTab('system')}
            >
              <RefreshCw size={18} className={activeTab === 'system' ? 'text-blue-400' : 'text-gray-400'} />
              <span className={activeTab === 'system' ? 'text-blue-400' : 'text-gray-400'}>System</span>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="flex-1 overflow-y-auto bg-gray-850">
            <form onSubmit={handleSubmit} className="flex flex-col h-full">
              {/* Tab Content */}
              <div className="flex-1 p-6">
                {activeTab === 'profile' && (
                  <div className="space-y-6">
                    {/* User Avatar */}
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="h-20 w-20 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-2xl font-bold text-white">
                          {formData.first_name.charAt(0)}{formData.last_name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white">
                          {formData.first_name} {formData.last_name}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {formData.email}
                        </p>
                      </div>
                    </div>
                    
                    {/* Basic Info Section */}
                    <div className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800/50 backdrop-blur-sm">
                      <div 
                        className="flex items-center justify-between px-5 py-3 bg-gray-750 cursor-pointer"
                        onClick={() => toggleSection('basicInfo')}
                      >
                        <h3 className="text-gray-200 font-medium flex items-center">
                          <User size={16} className="mr-2 text-blue-400" />
                          Basic Information
                        </h3>
                        {expandedSections.basicInfo ? (
                          <ChevronDown size={16} className="text-gray-400" />
                        ) : (
                          <ChevronRight size={16} className="text-gray-400" />
                        )}
                      </div>
                      
                      {expandedSections.basicInfo && (
                        <div className="p-5 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <label className="text-sm text-gray-400">First Name</label>
                              <div className={`relative ${errors.first_name ? 'animate-shake' : ''}`}>
                                <input
                                  type="text"
                                  name="first_name"
                                  value={formData.first_name}
                                  onChange={handleChange}
                                  className={`w-full px-4 py-2.5 bg-gray-700 border rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-shadow ${errors.first_name ? 'border-red-500' : 'border-gray-600'}`}
                                />
                                {errors.first_name && (
                                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500">
                                    <AlertCircle size={16} />
                                  </div>
                                )}
                              </div>
                              {errors.first_name && (
                                <p className="text-xs text-red-400 mt-1">{errors.first_name}</p>
                              )}
                            </div>
                            
                            <div className="space-y-1">
                              <label className="text-sm text-gray-400">Last Name</label>
                              <div className={`relative ${errors.last_name ? 'animate-shake' : ''}`}>
                                <input
                                  type="text"
                                  name="last_name"
                                  value={formData.last_name}
                                  onChange={handleChange}
                                  className={`w-full px-4 py-2.5 bg-gray-700 border rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-shadow ${errors.last_name ? 'border-red-500' : 'border-gray-600'}`}
                                />
                                {errors.last_name && (
                                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500">
                                    <AlertCircle size={16} />
                                  </div>
                                )}
                              </div>
                              {errors.last_name && (
                                <p className="text-xs text-red-400 mt-1">{errors.last_name}</p>
                              )}
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <label className="text-sm text-gray-400">Username</label>
                            <div className={`relative ${errors.username ? 'animate-shake' : ''}`}>
                              <input
                                type="text"
                                name="username"
                                value={formData.username}
                                readOnly
                                onChange={handleChange}
                                className={`w-full px-4 py-2.5 bg-gray-700 border rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-shadow ${errors.username ? 'border-red-500' : 'border-gray-600'}`}
                              />
                              {errors.username && (
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500">
                                  <AlertCircle size={16} />
                                </div>
                              )}
                            </div>
                            {errors.username && (
                              <p className="text-xs text-red-400 mt-1">{errors.username}</p>
                            )}
                          </div>
                          
                          <div className="space-y-1">
                            <label className="text-sm text-gray-400">Bio</label>
                            <textarea
                              name="bio"
                              value={formData.bio}
                              onChange={handleChange}
                              rows="3"
                              className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-shadow"
                            ></textarea>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Contact Info Section */}
                    <div className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800/50 backdrop-blur-sm">
                      <div 
                        className="flex items-center justify-between px-5 py-3 bg-gray-750 cursor-pointer"
                        onClick={() => toggleSection('contactInfo')}
                      >
                        <h3 className="text-gray-200 font-medium flex items-center">
                          <Mail size={16} className="mr-2 text-blue-400" />
                          Contact Information
                        </h3>
                        {expandedSections.contactInfo ? (
                          <ChevronDown size={16} className="text-gray-400" />
                        ) : (
                          <ChevronRight size={16} className="text-gray-400" />
                        )}
                      </div>
                      
                      {expandedSections.contactInfo && (
                        <div className="p-5 space-y-4">
                          <div className="space-y-1">
                            <label className="text-sm text-gray-400 flex items-center">
                                <Mail size={14} className="mr-2 text-gray-500" />
                                Email
                            </label>
                            <div className={`relative ${errors.email ? 'animate-shake' : ''}`}>
                                <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`w-full px-4 py-2.5 bg-gray-700 border rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-shadow ${errors.email ? 'border-red-500' : 'border-gray-600'}`}
                                />
                                {errors.email && (
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500">
                                    <AlertCircle size={16} />
                                </div>
                                )}
                            </div>
                            {errors.email && (
                                <p className="text-xs text-red-400 mt-1">{errors.email}</p>
                            )}
                            </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <label className="text-sm text-gray-400 flex items-center">
                                <Phone size={14} className="mr-2 text-gray-500" />
                                Phone Number
                              </label>
                              <div className={`relative ${errors.phone_number ? 'animate-shake' : ''}`}>
                                <input
                                  type="tel"
                                  name="phone_number"
                                  value={formData.phone_number}
                                  onChange={handleChange}
                                  placeholder="+1 (XXX) XXX-XXXX"
                                  className={`w-full px-4 py-2.5 bg-gray-700 border rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-shadow ${errors.phone_number ? 'border-red-500' : 'border-gray-600'}`}
                                />
                                {errors.phone_number && (
                                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500">
                                    <AlertCircle size={16} />
                                  </div>
                                )}
                              </div>
                              {errors.phone_number && (
                                <p className="text-xs text-red-400 mt-1">{errors.phone_number}</p>
                              )}
                            </div>
                            
                            <div className="space-y-1">
                              <label className="text-sm text-gray-400 flex items-center">
                                <Calendar size={14} className="mr-2 text-gray-500" />
                                Date of Birth
                              </label>
                              <input
                                type="date"
                                name="date_of_birth"
                                value={formData.date_of_birth}
                                onChange={handleChange}
                                className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-shadow"
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <label className="text-sm text-gray-400 flex items-center">
                              <MapPin size={14} className="mr-2 text-gray-500" />
                              Address
                            </label>
                            <input
                              type="text"
                              name="address"
                              value={formData.address}
                              onChange={handleChange}
                              className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-shadow"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {activeTab === 'security' && (
                    <div className="space-y-6">
                        {/* Security Info Section */}
                        <div className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800/50 backdrop-blur-sm">
                        <div 
                            className="flex items-center justify-between px-5 py-3 bg-gray-750 cursor-pointer"
                            onClick={() => toggleSection('securityInfo')}
                        >
                            <h3 className="text-gray-200 font-medium flex items-center">
                            <Shield size={16} className="mr-2 text-blue-400" />
                            Security Information
                            </h3>
                            {expandedSections.securityInfo ? (
                            <ChevronDown size={16} className="text-gray-400" />
                            ) : (
                            <ChevronRight size={16} className="text-gray-400" />
                            )}
                        </div>
                        
                        {expandedSections.securityInfo && (
                            <div className="p-5 space-y-6">
                            {/* Roles Management */}
                            <div className="space-y-3">
                                <h4 className="text-gray-300 font-medium flex items-center">
                                <UserCheck size={16} className="mr-2 text-blue-400" />
                                Roles Management
                                </h4>
                                <div className="bg-gray-750 rounded-lg border border-gray-700 p-4">
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {formData.roles?.map((role, index) => (
                                    <div key={index} className="flex items-center bg-gray-700 px-3 py-1 rounded-full">
                                        <span className="text-gray-200 text-sm">{role}</span>
                                        <button
                                        type="button"
                                        onClick={() => {
                                            const newRoles = formData.roles.filter(r => r !== role);
                                            setFormData({ ...formData, roles: newRoles });
                                        }}
                                        className="ml-2 text-gray-400 hover:text-red-400"
                                        >
                                        <X size={14} />
                                        </button>
                                    </div>
                                    ))}
                                </div>
                                <div className="flex">
                                    <select
                                    value="newRoles"
                                    onChange={(e) => {
                                        if (e.target.value && !formData.roles.includes(e.target.value)) {
                                        setFormData({
                                            ...formData,
                                            roles: [...formData.roles, e.target.value]
                                        });
                                        }
                                    }}
                                    className="flex-1 bg-gray-700 border border-gray-600 rounded-l-lg text-gray-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                                    >
                                    <option value="">Select a role to add</option>
                                    {roles.map(role => (
                                        <option key={role.id} value={role.name}>{role.name}</option>
                                    ))}
                                    </select>
                                    <button
                                    type="button"
                                    disabled
                                    onClick={() => {
                                        const select = document.querySelector('select[name="newRole"]');
                                        if (select.value && !formData.roles.includes(select.value)) {
                                        setFormData({
                                            ...formData,
                                            roles: [...formData.roles, select.value]
                                        });
                                        }
                                    }}
                                    className="bg-blue-600 hover:bg-blue-500 px-3 py-2 rounded-r-lg text-white"
                                    >
                                    Add
                                    </button>
                                </div>
                                </div>
                            </div>

                            {/* Tools Management */}
                            <div className="space-y-3">
                                <h4 className="text-gray-300 font-medium flex items-center">
                                <Settings size={16} className="mr-2 text-blue-400" />
                                Tools Access
                                </h4>
                                <div className="bg-gray-750 rounded-lg border border-gray-700 p-4">
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {formData.tools?.map((tool, index) => (
                                    <div key={index} className="flex items-center bg-gray-700 px-3 py-1 rounded-full">
                                        <span className="text-gray-200 text-sm">{tool}</span>
                                        <button
                                        type="button"
                                        onClick={() => {
                                            const newTools = formData.tools.filter(t => t !== tool);
                                            setFormData({ ...formData, tools: newTools });
                                        }}
                                        className="ml-2 text-gray-400 hover:text-red-400"
                                        >
                                        <X size={14} />
                                        </button>
                                    </div>
                                    ))}
                                </div>
                                <div className="flex">
                                    <select
                                    value="newTools"
                                    onChange={(e) => {
                                        if (e.target.value && !formData.tools.includes(e.target.value)) {
                                        setFormData({
                                            ...formData,
                                            tools: [...formData.tools, e.target.value]
                                        });
                                        }
                                    }}
                                    className="flex-1 bg-gray-700 border border-gray-600 rounded-l-lg text-gray-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                                    >
                                    <option value="">Select a tool to add</option>
                                    {tools.map(tool => (
                                        <option key={tool.id} value={tool.name}>{tool.name}</option>
                                    ))}
                                    </select>
                                    <button
                                    type="button"
                                    disabled
                                    onClick={() => {
                                        const select = document.querySelector('select[name="newTool"]');
                                        if (select.value && !formData.tools.includes(select.value)) {
                                        setFormData({
                                            ...formData,
                                            tools: [...formData.tools, select.value]
                                        });
                                        }
                                    }}
                                    className="bg-blue-600 hover:bg-blue-500 px-3 py-2 rounded-r-lg text-white"
                                    >
                                    Add
                                    </button>
                                </div>
                                </div>
                            </div>

                            {/* Account Status */}
                            <div className="flex items-center justify-between p-4 bg-gray-750 rounded-lg border border-gray-700">
                                <div className="flex items-center">
                                <div className="mr-4 bg-green-500/20 p-2 rounded-lg">
                                    <CheckCircle size={20} className="text-green-400" />
                                </div>
                                <div>
                                    <h4 className="text-gray-200 font-medium">Account Status</h4>
                                    <p className="text-gray-400 text-sm">Enable or disable account access</p>
                                </div>
                                </div>
                                <label className="flex items-center cursor-pointer">
                                <div className="relative">
                                    <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleChange}
                                    className="sr-only"
                                    />
                                    <div className={`w-12 h-6 rounded-full transition-colors ${formData.is_active ? 'bg-green-500' : 'bg-gray-600'}`}></div>
                                    <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.is_active ? 'transform translate-x-6' : ''}`}></div>
                                </div>
                                <span className="ml-3 text-gray-300 text-sm">
                                    {formData.is_active ? 'Active' : 'Inactive'}
                                </span>
                                </label>
                            </div>
                            </div>
                        )}
                        </div>
                    </div>
                    )}
                
                {activeTab === 'preferences' && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center p-6 bg-gray-800/50 border border-gray-700 rounded-lg">
                      <Settings size={40} className="mx-auto text-gray-500 mb-4" />
                      <h3 className="text-gray-300 font-medium mb-2">User Preferences</h3>
                      <p className="text-gray-400 text-sm">User preference settings will be available in a future update.</p>
                    </div>
                  </div>
                )}
                
                {activeTab === 'system' && (
                  <div className="space-y-6">
                    {/* System Info Section */}
                    <div className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800/50 backdrop-blur-sm">
                      <div 
                        className="flex items-center justify-between px-5 py-3 bg-gray-750 cursor-pointer"
                        onClick={() => toggleSection('systemInfo')}
                      >
                        <h3 className="text-gray-200 font-medium flex items-center">
                          <RefreshCw size={16} className="mr-2 text-blue-400" />
                          System Information
                        </h3>
                        {expandedSections.systemInfo ? (
                          <ChevronDown size={16} className="text-gray-400" />
                        ) : (
                          <ChevronRight size={16} className="text-gray-400" />
                        )}
                      </div>
                      
                      {expandedSections.systemInfo && (
                        <div className="p-5 space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="bg-gray-750 p-4 rounded-lg border border-gray-700 flex items-center">
                              <div className="mr-4 bg-blue-500/20 p-2 rounded-lg">
                                <Clock size={20} className="text-blue-400" />
                              </div>
                              <div>
                                <h4 className="text-gray-400 text-xs uppercase font-medium">Created</h4>
                                <p className="text-gray-200">
                                  {user?.created_at ? new Date(user.created_at).toLocaleString() : 'N/A'}
                                </p>
                              </div>
                            </div>
                            
                            <div className="bg-gray-750 p-4 rounded-lg border border-gray-700 flex items-center">
                              <div className="mr-4 bg-purple-500/20 p-2 rounded-lg">
                                <RefreshCw size={20} className="text-purple-400" />
                              </div>
                              <div>
                                <h4 className="text-gray-400 text-xs uppercase font-medium">Last Updated</h4>
                                <p className="text-gray-200">
                                  {user?.updated_at ? new Date(user.updated_at).toLocaleString() : 'N/A'}
                                </p>
                              </div>
                            </div>
                            
                            <div className="bg-gray-750 p-4 rounded-lg border border-gray-700 flex items-center">
                              <div className="mr-4 bg-green-500/20 p-2 rounded-lg">
                                <UserCheck size={20} className="text-green-400" />
                              </div>
                              <div>
                                <h4 className="text-gray-400 text-xs uppercase font-medium">Last Login</h4>
                                <p className="text-gray-200">
                                  {user?.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                                </p>
                              </div>
                            </div>
                            
                            <div className="bg-gray-750 p-4 rounded-lg border border-gray-700 flex items-center">
                              <div className="mr-4 bg-yellow-500/20 p-2 rounded-lg">
                                <Fingerprint size={20} className="text-yellow-400" />
                              </div>
                              <div>
                                <h4 className="text-gray-400 text-xs uppercase font-medium">Biometric Status</h4>
                                <p className="text-gray-200">
                                  {user?.facial_data ? 'Registered' : 'Not Registered'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="flex justify-between items-center px-6 py-4 border-t border-gray-700 bg-gray-800/90 backdrop-blur-sm">
                <div className="text-gray-400 text-sm">
                  <span className="text-blue-400">*</span> Required fields
                </div>
                <div className="flex space-x-3">
                  <button 
                    type="button" 
                    onClick={onClose} 
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={isSaving} 
                    className={`px-4 py-2 ${isSaving ? 'bg-gray-600' : 'bg-blue-500 hover:bg-blue-400'} rounded-lg text-sm text-white transition-colors`}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
                {showConfirmation && (
                  <div className="absolute top-0 right-0 m-4 p-2 bg-green-500 text-white rounded-lg shadow-lg animate-fade-in">
                   New User profile send !
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserEditModal;
